import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, KeyRound, Radio, BookOpen, ArrowUpRight, Receipt, Copy, ShieldOff, Loader2, ExternalLink, DownloadCloud, Bell, PlayCircle, Award, FileDown, TrendingUp, TrendingDown, MinusCircle,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadFile } from "@/lib/download";
import AccountSecurity from "@/pages/AccountSecurity";

const TABS = [
  { k: "overview", label: "Overview" },
  { k: "indicators", label: "Indicators" },
  { k: "expert-advisors", label: "Expert Advisors" },
  { k: "signals", label: "Signals" },
  { k: "courses", label: "Courses" },
  { k: "security", label: "Security" },
];

export default function UserDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [orders, setOrders] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [sub, setSub] = useState(null);
  const [signalFeed, setSignalFeed] = useState([]);
  const [feedMore, setFeedMore] = useState(false);
  const [feedBusy, setFeedBusy] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [aff, setAff] = useState(null);
  const [rebindReqs, setRebindReqs] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [o, l] = await Promise.all([api.get("/orders/me"), api.get("/licenses/me")]);
      setOrders(o.data);
      setLicenses(l.data);
      try { const { data } = await api.get("/signals/subscription/me"); setSub(data); } catch { setSub(null); }
      try { const { data } = await api.get("/signals/feed"); setSignalFeed(data.posts || []); setFeedMore(!!data.has_more); } catch { setSignalFeed([]); }
      try { const { data } = await api.get("/notifications/me?limit=10"); setNotifs(data.items || []); } catch { setNotifs([]); }
      try { const { data } = await api.get("/affiliate/me"); setAff(data); } catch { setAff(null); }
      try {
        const { data } = await api.get("/licenses/me/rebind-requests");
        const m = {}; (data || []).forEach((r) => { if (r.status === "requested") m[r.license_id] = true; });
        setRebindReqs(m);
      } catch { setRebindReqs({}); }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadMoreSignals = async () => {
    setFeedBusy(true);
    try {
      const { data } = await api.get(`/signals/feed?offset=${signalFeed.length}`);
      setSignalFeed((p) => [...p, ...(data.posts || [])]);
      setFeedMore(!!data.has_more);
    } catch { /* silent */ }
    finally { setFeedBusy(false); }
  };

  const paidOrders = orders.filter((o) => o.status === "paid");
  const spent = paidOrders.reduce((s, o) => s + o.total, 0);
  const activeLicenses = licenses.filter((l) => l.status === "active").length;

  const indicators = licenses.filter((l) => l.category_slug === "indicators");
  const eas = licenses.filter((l) => l.category_slug === "expert-advisors");
  const courses = licenses.filter((l) => l.category_slug === "courses");

  return (
    <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8 lg:py-20">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-3">Operator’s desk</p>
          <h1 data-testid="dashboard-title" className="font-editorial text-4xl font-medium tracking-tight text-white sm:text-5xl">
            Welcome, <span className="italic text-emerald-300">{user?.full_name?.split(" ")[0] || "trader"}</span>.
          </h1>
        </div>
        <span className="chip chip-emerald">{user?.email}</span>
      </div>

      {/* Tabs */}
      <div className="mb-8 flex flex-wrap gap-2 border-b border-emerald-500/10 pb-3" data-testid="dashboard-tabs">
        {TABS.map((t) => {
          const count = t.k === "indicators" ? indicators.length : t.k === "expert-advisors" ? eas.length : t.k === "courses" ? courses.length : null;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`dashboard-tab-${t.k}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.k ? "bg-emerald-500/15 text-lime-200" : "text-emerald-100/60 hover:text-white"
              }`}>
              {t.label}{count !== null ? ` (${count})` : ""}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <>
          <div className="mb-8 grid gap-4 md:grid-cols-4">
            {[
              { k: "Active licenses", v: activeLicenses, icon: KeyRound },
              { k: "Owned products", v: licenses.length, icon: Package },
              { k: "Total spent", v: `$${spent.toFixed(2)}`, icon: Receipt },
              { k: "Signals subscription", v: sub?.active ? "Active" : "—", icon: Radio },
            ].map((m, i) => (
              <motion.div key={m.k} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="card-moss p-5">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-300"><m.icon size={16} /></span>
                <p className="overline mt-4">{m.k}</p>
                <p className="mt-1 font-editorial text-3xl text-white">{loading ? "…" : m.v}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {aff?.is_affiliate && (
              <div className="lg:col-span-2 card-moss flex flex-wrap items-center justify-between gap-4 p-6" data-testid="dashboard-affiliate-card">
                <div>
                  <p className="overline mb-1">Affiliate · {aff.commission_rate}% commission</p>
                  <p className="text-sm text-emerald-100/70">You've earned <span className="font-editorial text-lime-300">${Number(aff.stats?.owed || 0).toFixed(2)}</span> owed · <span className="text-white">${Number(aff.stats?.earned_total || 0).toFixed(2)}</span> total.</p>
                </div>
                <Link to="/affiliate" className="btn-edge text-sm" data-testid="dashboard-affiliate-link">Open affiliate desk <ArrowUpRight size={14} /></Link>
              </div>
            )}
            {/* Updates / notifications */}
            <div className="card-edge overflow-hidden" data-testid="dashboard-updates">
              <div className="flex items-center justify-between border-b border-emerald-500/10 px-6 py-5">
                <div className="flex items-center gap-2"><Bell size={16} className="text-lime-300" /><p className="overline">Product updates</p></div>
              </div>
              {notifs.length === 0 ? (
                <p className="p-6 text-sm text-emerald-100/60">No updates yet. When a product you own ships a new version, it appears here.</p>
              ) : (
                <div className="divide-y divide-emerald-500/8">
                  {notifs.map((n) => (
                    <div key={n.id} className="px-6 py-4" data-testid={`dashboard-update-${n.id}`}>
                      <p className="text-sm font-medium text-white">{n.title}</p>
                      <p className="mt-0.5 text-xs text-emerald-100/60">{n.body}</p>
                      <div className="mt-2 flex items-center gap-3">
                        {n.product_slug && (
                          <Link to={`/product/${n.product_slug}`} className="inline-flex items-center gap-1 text-xs text-lime-300 hover:text-lime-200">
                            View product <ArrowUpRight size={12} />
                          </Link>
                        )}
                        <span className="text-[10px] text-emerald-100/40">{new Date(n.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Purchase history */}
            <div className="card-edge overflow-hidden">
              <div className="flex items-center justify-between border-b border-emerald-500/10 px-6 py-5">
                <div className="flex items-center gap-2"><Receipt size={16} className="text-emerald-300" /><p className="overline">Purchase history</p></div>
              </div>
              {orders.length === 0 ? (
                <p className="p-6 text-sm text-emerald-100/60">No orders yet.</p>
              ) : (
                <div className="divide-y divide-emerald-500/8">
                  {orders.slice(0, 6).map((o) => (
                    <div key={o.id} className="flex items-center justify-between gap-3 px-6 py-3 text-sm">
                      <div>
                        <p className="text-white">{o.items.map((i) => i.title).join(", ")}</p>
                        <p className="text-[11px] text-emerald-100/50">
                          <span className={`chip ${o.status === "paid" ? "chip-emerald" : o.status === "failed" ? "chip-amber" : "chip-neutral"}`}>{o.status}</span>
                          {" · "}{new Date(o.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                      <Link to={`/orders/${o.id}`} className="text-emerald-300 hover:text-lime-300"><ArrowUpRight size={14} /></Link>
                      </div>
                      {o.status === "paid" && (
                        <button onClick={() => downloadFile(`/orders/${o.id}/receipt`, `receipt-${o.id.slice(0, 8)}.pdf`)} data-testid={`receipt-${o.id}`}
                          className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/20 px-2.5 py-1 text-[11px] text-emerald-100/80 hover:border-lime-400/50 hover:text-lime-300">
                          <FileDown size={12} /> Receipt
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {(tab === "indicators" || tab === "expert-advisors") && (
        <LicenseList
          items={tab === "indicators" ? indicators : eas}
          loading={loading}
          onChange={load}
          rebindReqs={rebindReqs}
          emptyLabel={tab === "indicators" ? "indicator" : "Expert Advisor"}
          browseTo={tab === "indicators" ? "/store/indicators" : "/store/expert-advisors"}
        />
      )}

      {tab === "signals" && (
        <div className="space-y-6" data-testid="dashboard-signals">
          <div className="max-w-xl card-moss p-6">
            <div className="flex items-center justify-between">
              <p className="overline">Signals subscription</p>
              <Radio size={16} className="text-lime-300" />
            </div>
            {sub?.active ? (
              <>
                <p className="mt-2 font-editorial text-2xl text-white">Active</p>
                <p className="mt-1 text-xs text-emerald-100/60">Renews after <span className="font-mono-edge">{new Date(sub.current_period_end).toLocaleDateString()}</span></p>
                {sub.telegram_invite_url && (
                  <a href={sub.telegram_invite_url} target="_blank" rel="noreferrer" data-testid="dashboard-telegram" className="btn-edge mt-4 text-xs">
                    Join Telegram <ExternalLink size={14} />
                  </a>
                )}
                <p className="mt-3 text-[11px] leading-relaxed text-emerald-100/50">
                  Tip: link your Telegram in the <span className="text-emerald-100/70">Security</span> tab first. Then tap Join —
                  you're approved automatically while your subscription stays active, and removed when it lapses.
                </p>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm text-emerald-100/70">Not active. Subscribe on the Signals page to get Telegram access + your live signal feed here.</p>
                <Link to="/signals" className="btn-edge mt-4 text-xs" data-testid="dashboard-signals-cta">See signals <ArrowUpRight size={14} /></Link>
              </>
            )}
          </div>

          {sub?.active && (
            <div data-testid="dashboard-signal-feed">
              <p className="overline mb-3">Latest signals</p>
              {signalFeed.length === 0 ? (
                <div className="card-edge p-6"><p className="text-sm text-emerald-100/70">No signals published yet. You’ll get a Telegram push the moment we post one.</p></div>
              ) : (
                <div className="grid gap-3 lg:grid-cols-2">
                  {signalFeed.map((s) => <DashSignalCard key={s.id} s={s} />)}
                </div>
              )}
              {feedMore && (
                <button onClick={loadMoreSignals} disabled={feedBusy} data-testid="dashboard-signals-load-more"
                  className="btn-edge-ghost mt-4 w-full justify-center text-xs">
                  {feedBusy ? <Loader2 size={14} className="animate-spin" /> : null} Load older signals
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "security" && <AccountSecurity />}

      {tab === "courses" && (
        <div data-testid="dashboard-courses">
          {courses.length === 0 ? (
            <div className="card-edge p-8">
              <p className="text-sm text-emerald-100/70">You haven’t enrolled in a course yet.</p>
              <Link to="/store/courses" className="btn-edge mt-6" data-testid="dashboard-courses-cta">Browse courses <ArrowUpRight size={14} /></Link>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {courses.map((c) => {
                const pct = c.lesson_count ? Math.round((c.completed_count / c.lesson_count) * 100) : 0;
                const started = (c.completed_count || 0) > 0;
                return (
                  <div key={c.id} className="card-moss flex flex-col gap-4 p-5" data-testid={`dashboard-course-${c.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-300"><BookOpen size={18} /></span>
                        <div>
                          <p className="font-editorial text-lg text-white">{c.product_title}</p>
                          <p className="text-[11px] text-emerald-100/50">{pct === 100 ? "Completed" : started ? "In progress" : "Not started yet"} · full access</p>
                        </div>
                      </div>
                      <Link to={`/course/${c.product_slug}`} data-testid={`course-continue-${c.id}`} className="btn-edge shrink-0 text-xs">
                        <PlayCircle size={14} /> {started && pct < 100 ? "Resume" : pct === 100 ? "Review" : "Start"}
                      </Link>
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-[11px] text-emerald-100/60">
                        <span>{c.completed_count || 0}/{c.lesson_count || 0} lessons</span>
                        <span className="font-semibold text-lime-300" data-testid={`dashboard-course-pct-${c.id}`}>{pct}%</span>
                      </div>
                      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#0d2416]/70" data-testid={`dashboard-course-bar-${c.id}`}>
                        <div className="h-full rounded-full bg-lime-400 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    {pct === 100 && c.certificate_code && (
                      <Link to={`/certificate/${c.certificate_code}`} data-testid={`dashboard-course-cert-${c.id}`}
                        className="inline-flex w-fit items-center gap-1.5 rounded-full border border-lime-400/40 bg-lime-400/10 px-3 py-1 text-xs font-medium text-lime-300 hover:bg-lime-400/20">
                        <Award size={13} /> View certificate
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LicenseList({ items, loading, onChange, emptyLabel, browseTo, rebindReqs }) {
  if (loading) return <div className="card-edge p-8 text-sm text-emerald-100/50">Loading…</div>;
  if (items.length === 0) {
    return (
      <div className="card-edge p-8">
        <p className="text-sm text-emerald-100/70">No {emptyLabel} licenses yet. Buy one and your key + bind form + download arrive here automatically.</p>
        <Link to={browseTo} className="btn-edge mt-6" data-testid="license-empty-cta">Browse store <ArrowUpRight size={14} /></Link>
      </div>
    );
  }
  return (
    <div className="card-edge divide-y divide-emerald-500/8 overflow-hidden">
      {items.map((l) => <LicenseRow key={l.id} l={l} onChange={onChange} pendingRebind={!!(rebindReqs && rebindReqs[l.id])} />)}
    </div>
  );
}

function LicenseRow({ l, onChange, pendingRebind }) {
  const [openBind, setOpenBind] = useState(false);
  const [account, setAccount] = useState(l.account_number || "");
  const [platform, setPlatform] = useState(l.platform || "MT5");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reqBusy, setReqBusy] = useState(false);
  const [reason, setReason] = useState("New broker");
  const rebindsUsedUp = (l.rebinds_used || 0) >= (l.rebinds_max ?? 3);

  const requestRebind = async () => {
    setReqBusy(true);
    try {
      await api.post(`/licenses/${l.id}/rebind-request`, { reason });
      toast.success("Rebind reset requested — an admin will review it.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setReqBusy(false); }
  };

  const bind = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/licenses/${l.id}/bind`, { account_number: account, platform });
      toast.success("License bound");
      setOpenBind(false);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard?.writeText(l.license_key); toast.success("License key copied"); };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/products/${l.product_slug}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : l.product_slug;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No download available for this product");
    } finally { setDownloading(false); }
  };

  return (
    <div className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`chip ${l.status === "active" ? "chip-emerald" : l.status === "revoked" ? "chip-amber" : "chip-neutral"}`}>{l.status}</span>
            {l.status === "revoked" && <ShieldOff size={12} className="text-amber-300" />}
            <span className="chip chip-neutral font-mono-edge" data-testid={`license-version-${l.id}`}>v{l.version || "1.0.0"}</span>
          </div>
          <p className="mt-2 font-editorial text-lg font-medium text-white">{l.product_title}</p>
          <button onClick={copy} data-testid={`license-copy-${l.id}`} className="mt-1 inline-flex items-center gap-1.5 font-mono-edge text-[11px] text-lime-300 hover:text-lime-200">
            {l.license_key} <Copy size={11} />
          </button>
          {l.account_number && (
            <p className="mt-1 text-[11px] text-emerald-100/50">Bound to {l.platform} · {l.account_number} · {l.rebinds_used}/{l.rebinds_max} rebinds used</p>
          )}
          {l.expires_at && (
            <p className={`mt-1 text-[11px] ${l.sub_status === "expired" ? "text-amber-300" : l.sub_status === "grace" ? "text-amber-200" : "text-emerald-100/50"}`} data-testid={`license-expiry-${l.id}`}>
              {l.sub_status === "expired" ? "Expired" : l.sub_status === "grace" ? "In grace period — renew now" : "Renews"} · {new Date(l.expires_at).toLocaleDateString()}
              {l.product_slug && (l.sub_status === "expired" || l.sub_status === "grace") && (
                <Link to={`/product/${l.product_slug}`} data-testid={`license-renew-${l.id}`} className="ml-2 font-medium text-lime-300 hover:text-lime-200">Renew →</Link>
              )}
            </p>
          )}
        </div>
        {l.status !== "revoked" && (
          <div className="flex flex-col items-end gap-2">
            <button onClick={() => setOpenBind((v) => !v)} data-testid={`license-bind-toggle-${l.id}`} className="btn-edge-ghost text-xs">
              {l.account_number ? "Rebind" : "Bind account"}
            </button>
            {rebindsUsedUp && (
              pendingRebind ? (
                <span className="chip chip-amber" data-testid={`license-rebind-pending-${l.id}`}>Reset requested</span>
              ) : (
                <div className="flex items-center gap-1.5">
                  <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`license-rebind-reason-${l.id}`}
                    className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-2 py-1 text-[11px] text-white focus:border-emerald-400/60 focus:outline-none">
                    <option value="New broker">New broker</option>
                    <option value="Lost account">Lost account</option>
                    <option value="New device/VPS">New device/VPS</option>
                    <option value="Other">Other</option>
                  </select>
                  <button onClick={requestRebind} disabled={reqBusy} data-testid={`license-rebind-request-${l.id}`} className="text-[11px] text-lime-300 hover:text-lime-200 disabled:opacity-50">
                    {reqBusy ? "Requesting…" : "Request reset"}
                  </button>
                </div>
              )
            )}
          </div>
        )}
      </div>
      <div className="mt-3">
        <button onClick={download} disabled={downloading} data-testid={`license-download-${l.id}`} className="inline-flex items-center gap-1.5 text-xs text-lime-300 hover:text-lime-200 disabled:opacity-50">
          {downloading ? <Loader2 size={12} className="animate-spin" /> : <DownloadCloud size={12} />} Download latest file (v{l.version || "1.0.0"})
        </button>
      </div>
      {openBind && (
        <form onSubmit={bind} className="mt-4 grid gap-2 rounded-lg border border-emerald-500/12 bg-[#0d2416]/40 p-3 sm:grid-cols-[110px_1fr_auto]">
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid={`license-platform-${l.id}`}
            className="rounded-lg border border-emerald-500/15 bg-[#061510]/60 px-3 py-2 text-sm text-white">
            <option value="MT4">MT4</option>
            <option value="MT5">MT5</option>
          </select>
          <input value={account} onChange={(e) => setAccount(e.target.value)} required
            placeholder="Account number (e.g. 8842991)" data-testid={`license-account-${l.id}`}
            className="rounded-lg border border-emerald-500/15 bg-[#061510]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
          <button disabled={busy} data-testid={`license-bind-submit-${l.id}`} className="btn-edge text-xs">
            {busy ? <Loader2 size={12} className="animate-spin" /> : "Confirm"}
          </button>
        </form>
      )}
    </div>
  );
}

const DASH_OUTCOMES = {
  tp: { label: "✅ TP hit", cls: "chip-emerald" },
  sl: { label: "❌ SL hit", cls: "chip-amber" },
  be: { label: "⚪ Breakeven", cls: "chip-neutral" },
  cancelled: { label: "🚫 Cancelled", cls: "chip-neutral" },
};

function DashSignalCard({ s }) {
  const Dir = s.direction === "SELL" ? TrendingDown : s.direction === "HOLD" ? MinusCircle : TrendingUp;
  const tone = s.direction === "SELL" ? "text-red-300" : s.direction === "HOLD" ? "text-emerald-100/70" : "text-lime-300";
  return (
    <div className="card-edge p-5" data-testid={`dash-signal-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1 font-mono-edge text-xs uppercase tracking-widest ${tone}`}><Dir size={12} /> {s.direction}</span>
          {s.symbol && <span className="chip chip-neutral">{s.symbol}</span>}
          {s.outcome && DASH_OUTCOMES[s.outcome] && <span className={`chip ${DASH_OUTCOMES[s.outcome].cls}`} data-testid={`dash-signal-outcome-${s.id}`}>{DASH_OUTCOMES[s.outcome].label}</span>}
          {(s.tags || []).map((t) => <span key={t} className="chip chip-emerald">{t}</span>)}
        </div>
        <span className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/40">{new Date(s.published_at).toLocaleDateString()}</span>
      </div>
      <h3 className="mt-2 font-editorial text-lg text-white">{s.title}</h3>
      {s.body && <p className="mt-1 text-sm text-emerald-100/80">{s.body}</p>}
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        {s.entry && <DashStat k="Entry" v={s.entry} />}
        {s.sl && <DashStat k="SL" v={s.sl} />}
        {s.tp && <DashStat k="TP" v={s.tp} />}
      </div>
    </div>
  );
}
function DashStat({ k, v }) {
  return (
    <div className="rounded-lg border border-emerald-500/12 bg-[#0d2416]/50 p-2.5">
      <p className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/50">{k}</p>
      <p className="text-sm text-white">{v}</p>
    </div>
  );
}
